import { useEffect, useState } from 'react';

import { TIME_FORMATS } from 'constants/timeformats';
import { dateFormat as dateFmt, getDateTime, getDiff, timeNow, toUTC } from 'helpers/day';
import { dd_mm_hh as ddMmHh } from 'helpers/utils';
import {
  fundingIntervalState,
  nextFundingRealizationState,
} from 'selectors/priceSelectors';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { useAppSelector } from 'storeHooks';

const formatCountdown = (epochTime: number): string => {
  const now = toUTC(timeNow());

  const end = toUTC(getDateTime(epochTime));

  if (now >= end) return '';

  const duration = getDiff(end, now, 'milliseconds');

  if (duration < 86400 * 1000) {
    return dateFmt(toUTC(getDateTime(duration)), TIME_FORMATS.HHhmmmsss);
  }
  return ddMmHh(now, end);
};

const useNextFundingTimer = () => {
  const expiryIntervalFromApi = useAppSelector(
    state => selectedProductSelector(state)?.product_specs?.rate_exchange_interval || 0
  );
  const fundingIntervalInMs = (fundingIntervalState() || expiryIntervalFromApi) * 1000;
  const nextFundingInMs = nextFundingRealizationState() / 1000; // dividing by 1000 because time is coming in microseconds

  const [countDownValue, setCountDownValue] = useState(
    formatCountdown(nextFundingInMs) || '-'
  );

  useEffect(() => {
    let fallbackNextFundingRealization = 0;
    const setTime = setInterval(() => {
      let nextCountDownValue = formatCountdown(nextFundingInMs);

      if (!nextCountDownValue) {
        if (!fallbackNextFundingRealization) {
          fallbackNextFundingRealization = Date.now() + fundingIntervalInMs;
        }
        nextCountDownValue = formatCountdown(fallbackNextFundingRealization);
      }
      setCountDownValue(nextCountDownValue);
    }, 1000);

    return () => {
      clearInterval(setTime);
      fallbackNextFundingRealization = 0;
    };
  }, [fundingIntervalInMs, nextFundingInMs]);

  return countDownValue;
};

export { useNextFundingTimer };
